import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';

interface CustomerSource {
  id: string;
  customer_id: string;
  source_type: string;
  status: 'active' | 'inactive' | 'error' | 'pending' | 'setup_required';
  configuration: Record<string, any>;
  last_sync_at?: string;
  last_error?: string;
  sync_frequency: 'daily' | 'weekly' | 'monthly' | 'manual';
  created_at: string;
  updated_at: string;
}

export const useSourceManager = (customerId: string) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [sources, setSources] = useState<Record<string, CustomerSource>>({});
  const [loading, setLoading] = useState(false);

  // Initialize default sources for customer
  const initializeCustomerSources = async () => {
    if (!user?.id || !customerId) return;
    
    const defaultSources = [
      { type: 'mailbox', status: 'active', config: { auto_enabled: true } },
      { type: 'portal', status: 'inactive', config: {} },
      { type: 'bulk', status: 'inactive', config: {} },
      { type: 'drive', status: 'inactive', config: {} },
      { type: 'smtp', status: 'inactive', config: {} },
      { type: 'api', status: 'inactive', config: {} },
      { type: 'manual', status: 'inactive', config: {} }
    ];

    for (const source of defaultSources) {
      await upsertSource(source.type, source.status, source.config);
    }
  };

  // Upsert source configuration
  const upsertSource = async (sourceType: string, status: string, config: any = {}) => {
    if (!user?.id || !customerId) return;

    try {
      const { data, error } = await supabase
        .from('customer_sources')
        .upsert({
          user_id: user.id,
          customer_id: customerId,
          source_type: sourceType,
          status: status,
          configuration: config,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'customer_id,source_type'
        })
        .select()
        .single();

      if (error) throw error;

      // Update local state
      setSources(prev => ({
        ...prev,
        [sourceType]: data as CustomerSource
      }));

      // Log the change
      await logSourceActivity(sourceType, 'source_updated', 'success', {
        status: status,
        config: config
      });

      return data;
    } catch (error) {
      console.error('Failed to update source:', error);
      throw error;
    }
  };

  // Enable a source
  const enableSource = async (sourceType: string, config: any = {}) => {
    setLoading(true);
    try {
      await upsertSource(sourceType, 'active', config);
      toast({
        title: "Source Enabled",
        description: `${sourceType} source enabled successfully!`,
      });
      
      // Trigger initial sync/setup if needed
      if (sourceType === 'portal' && config.portal_id) {
        await syncPortal(config.portal_id);
      }
    } catch (error) {
      toast({
        title: "Error",
        description: `Failed to enable ${sourceType} source`,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Disable a source
  const disableSource = async (sourceType: string) => {
    setLoading(true);
    try {
      await upsertSource(sourceType, 'inactive', {});
      toast({
        title: "Source Disabled",
        description: `${sourceType} source disabled successfully!`,
      });
    } catch (error) {
      toast({
        title: "Error",
        description: `Failed to disable ${sourceType} source`,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Test source connection
  const testSource = async (sourceType: string) => {
    setLoading(true);
    try {
      let testResult = false;
      
      switch (sourceType) {
        case 'mailbox':
          testResult = await testMailboxConnection();
          break;
        case 'portal':
          testResult = await testPortalConnection(sources[sourceType]?.configuration);
          break;
        case 'bulk':
          testResult = true; // Always available
          break;
        default:
          testResult = true; // Default to success for now
      }

      await logSourceActivity(sourceType, 'connection_test', 
        testResult ? 'success' : 'error', 
        { test_result: testResult }
      );

      if (testResult) {
        toast({
          title: "Test Successful",
          description: `${sourceType} connection test successful!`,
        });
      } else {
        toast({
          title: "Test Failed",
          description: `${sourceType} connection test failed!`,
          variant: "destructive",
        });
      }

      return testResult;
    } catch (error) {
      toast({
        title: "Test Failed",
        description: `${sourceType} test failed`,
        variant: "destructive",
      });
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Load customer sources
  const loadSources = async () => {
    if (!user?.id || !customerId) return;

    try {
      const { data, error } = await supabase
        .from('customer_sources')
        .select('*')
        .eq('customer_id', customerId)
        .eq('user_id', user.id);

      if (error) throw error;

      if (!data || data.length === 0) {
        // Initialize default sources if none exist
        await initializeCustomerSources();
      } else {
        // Convert array to object for easier access
        const sourcesObj: Record<string, CustomerSource> = {};
        data.forEach(source => {
          sourcesObj[source.source_type] = source as CustomerSource;
        });
        setSources(sourcesObj);
      }
    } catch (error) {
      console.error('Failed to load sources:', error);
    }
  };

  // Activity logging
  const logSourceActivity = async (sourceType: string, eventType: string, status: string, details: any) => {
    if (!user?.id) return;

    try {
      await supabase.from('ingestion_events').insert({
        user_id: customerId, // Use customer ID as user_id for filtering
        source_type: sourceType,
        event_type: eventType,
        status: status,
        details: {
          timestamp: new Date().toISOString(),
          ...details
        }
      });

      // Emit real-time event for immediate UI updates
      window.dispatchEvent(new CustomEvent('sourceActivity', {
        detail: { customerId, sourceType, eventType, status, details }
      }));
    } catch (error) {
      console.error('Failed to log activity:', error);
    }
  };

  // Test functions for different sources
  const testMailboxConnection = async () => {
    try {
      const response = await supabase.functions.invoke('admin-gmail-run-ingest', {
        body: { 
          testMode: true,
          customerFilter: customerId
        }
      });
      return response.data?.success || false;
    } catch (error) {
      return false;
    }
  };

  const testPortalConnection = async (config: any) => {
    try {
      if (!config?.portal_id) return false;
      
      // For now, just return true - portal testing would need specific implementation
      return true;
    } catch (error) {
      return false;
    }
  };

  const syncPortal = async (portalId: string) => {
    // Portal sync implementation would go here
    console.log('Syncing portal:', portalId);
  };

  useEffect(() => {
    if (customerId && user?.id) {
      loadSources();
    }
  }, [customerId, user?.id]);

  return {
    sources,
    loading,
    enableSource,
    disableSource,
    testSource,
    refreshSources: loadSources,
    logActivity: logSourceActivity
  };
};